import { useState, useRef, useEffect } from 'react';

export function useInlineEditing(
  card: { name: string; description?: string | null } | null,
  onSave: (name: string, description: string) => Promise<boolean>
) {
  // Inline editing state
  const [isEditingName, setIsEditingName] = useState(false);
  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [editedName, setEditedName] = useState('');
  const [editedDescription, setEditedDescription] = useState('');
  const nameInputRef = useRef<HTMLInputElement>(null);
  const descriptionInputRef = useRef<HTMLTextAreaElement>(null);

  // Focus input when editing starts
  useEffect(() => {
    if (isEditingName && nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, [isEditingName]);

  useEffect(() => {
    if (isEditingDescription && descriptionInputRef.current) {
      descriptionInputRef.current.focus();
    }
  }, [isEditingDescription]);

  // Start editing name
  const handleStartEditingName = () => {
    if (!card) return;
    setEditedName(card.name);
    setIsEditingName(true);
  };

  // Save name
  const handleSaveName = async () => {
    if (!card) return;
    const success = await onSave(editedName, card.description || '');
    if (success) {
      setIsEditingName(false);
    }
  };

  // Cancel editing name
  const handleCancelEditingName = () => {
    setIsEditingName(false);
    setEditedName(card?.name || '');
  };

  // Start editing description
  const handleStartEditingDescription = () => {
    if (!card) return;
    setEditedDescription(card.description || '');
    setIsEditingDescription(true);
  };

  // Save description
  const handleSaveDescription = async () => {
    if (!card) return;
    const success = await onSave(card.name, editedDescription);
    if (success) {
      setIsEditingDescription(false);
    }
  };

  // Cancel editing description
  const handleCancelEditingDescription = () => {
    setIsEditingDescription(false);
    setEditedDescription(card?.description || '');
  };

  // Handle key press for name input
  const handleNameKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSaveName();
    } else if (e.key === 'Escape') {
      handleCancelEditingName();
    }
  };

  // Handle key press for description input
  const handleDescriptionKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Escape') {
      handleCancelEditingDescription();
    }
  };

  return {
    // State
    isEditingName,
    isEditingDescription,
    editedName,
    editedDescription,
    nameInputRef,
    descriptionInputRef,
    // Handlers
    handleStartEditingName,
    handleSaveName,
    handleCancelEditingName,
    handleStartEditingDescription,
    handleSaveDescription,
    handleCancelEditingDescription,
    handleNameKeyPress,
    handleDescriptionKeyPress,
    // Setters
    setEditedName,
    setEditedDescription,
  };
}
